import type { ConversationMessage, OfficerRoleType, PlaceholderAnalysis } from '@/features/AI-documents/types/docGen.types';
import type { ChatFlow, ChatFlowContext, ChatFlowEventResult } from '@/features/startTask/preset_task/shared/preset/chatflows/types';

/**
 * Interim Dividends (B32113) — SG 일반 프리셋 챗플로우.
 *   타이틀 → 회사 선택 → 우측 스튜디오에서 결의서 + 주주별 Voucher 입력 → Create Task (주주구성 판별 없음).
 *   ⚠ categoryMatch 에 'dividend' 같은 넓은 단어를 넣지 않는다 — Final Dividends 와 섞인다(task code 로 매칭).
 */
export const interimDividendsFlow: ChatFlow = {
  id: 'sg-interim-dividends',
  taskCodeMatch: ['B32113'],
  categoryMatch: ['interim dividend', 'interim dividends'],

  getInitialMessages(preSet): ConversationMessage[] {
    const docCount = preSet.documents.length;
    return [
      {
        role: 'ai',
        content:
          `You selected **${preSet.task.TASK_NAME}**.\n\n` +
          (docCount > 0 ? `This preset includes ${docCount} document(s).\n\n` : '') +
          `First, enter a **Task Title**. Then choose the **company entity** — members and dividend vouchers are prepared automatically.`,
        timestamp: Date.now(),
        stepId: 1,
        source: 'system',
      },
    ];
  },

  steps: [
    {
      id: 'input-title',
      aiMessage: 'Please enter a Task Title.',
      widget: 'text-input',
      widgetConfig: { targetVarKey: '_taskTitle', targetEntityIndex: 0 },
      awaitEvent: true,
    },
    {
      id: 'select-entity',
      aiMessage: 'Search and select the **Singapore company** declaring the interim dividend.',
      widget: 'entity-selector',
      widgetConfig: {
        targetVarKey: '_entity',
        targetEntityIndex: 0,
        analysis: {
          success: true,
          placeholder: '_entity',
          sourceResolver: 'ENTITY',
          officerRole: null,
          varKey: '_entity',
          varLabel: 'Entity',
          confidence: 1,
          message: 'Select entity (Interim Dividends).',
          relatedFields: [],
          options: [],
        } as PlaceholderAnalysis,
      },
      awaitEvent: true,
    },
    {
      id: 'confirm',
      aiMessage:
        'Entity linked. A **dividend voucher** is prepared for each member on file (add or remove on the right). ' +
        'Enter the dividend details (amount, payment date) and click **Create Task**.',
      widget: null,
      nextStep: null,
    },
    {
      id: 'officer-list',
      aiMessage: 'Select a person from the entity.',
      widget: 'officer-list',
      widgetConfig: { targetVarKey: '_officer', targetEntityIndex: 0 },
      awaitEvent: true,
    },
    {
      id: 'officer-detail',
      aiMessage: 'Person selected.',
      widget: null,
      nextStep: 'confirm',
    },
  ],

  handleEvent(eventName: string, payload: any, context: ChatFlowContext): ChatFlowEventResult {
    if (eventName === 'value-submit' && context.currentStepId === 'input-title') {
      const title = typeof payload === 'string' ? payload : payload?.value || payload;
      if (!title || !String(title).trim()) return {};
      return {
        messages: [{ role: 'user', content: String(title), timestamp: Date.now(), stepId: 1 }],
        formUpdates: { taskTitle: String(title).trim() },
        nextStepId: 'select-entity',
      };
    }

    if (eventName === 'entity-select' && context.currentStepId === 'select-entity') {
      const ent = payload || {};
      const entityCd = ent.entityCd || ent.icpCd || '';
      const entityName = ent.entityName || ent.companyName || 'Entity';
      if (!entityCd) return {};
      return {
        messages: [{ role: 'user', content: `Selected: **${entityName}**`, timestamp: Date.now(), stepId: 1 }],
        formUpdates: {
          entityCd,
          entityName,
          icpCd: entityCd,
          registrationNo: ent.registrationNo || ent.regNo || '',
          companyAddress: ent.companyAddress || ent.address || '',
        },
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'request-person-lookup') {
      const ctx = payload || {};
      const fg = String(ctx.fieldGroup || '');
      const entityCd = context.entityCd || context.formData.entityCd || '';
      if (!entityCd) {
        return {
          messages: [{ role: 'ai', content: 'Please select an entity first.', timestamp: Date.now(), stepId: 1, source: 'system' }],
        };
      }
      const role = String(ctx.defaultRole || 'CONTACT');
      return {
        messages: [{ role: 'user', content: `Looking up **${fg || 'person'}** (role: ${role})...`, timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'officer-list',
        nextStepWidgetData: { entityCd, selectedRole: role as OfficerRoleType },
      };
    }

    if (eventName === 'officer-select' && context.currentStepId === 'officer-list') {
      const officer = payload || {};
      const officerName = officer.name || officer.contactName || 'Selected person';
      return {
        messages: [{ role: 'user', content: `Selected person: **${officerName}**`, timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'officer-detail',
      };
    }

    return {};
  },
};
